import React, { useState, useMemo, useCallback } from 'react'
import { AgGridReact } from 'ag-grid-react'
import { ColDef } from 'ag-grid-community'
import 'ag-grid-community/styles/ag-grid.css'
import 'ag-grid-community/styles/ag-theme-alpine.css'
import { Group } from '@/types'
import { Button } from '@/components/ui/Button'
import { GroupFormModal } from './GroupFormModal'
import { Modal } from '@/components/ui/Modal'
import { agGridLocaleFr } from '@/config/ag-grid-locale-fr'
import { AGTableWrapper } from '@/components/ag-grid/AGTableWrapper'
import { useGroupsStore } from '@/stores/groups.store'
import { useAuth } from '@/hooks/useAuth'
import { canManageClientGroups } from '@/utils/role-access'
import { getGroupsColumnDefs } from '@/column-defs/groups.column-defs'
import { toastError, toastSuccess } from '@/utils/toast'

export const GroupsTab: React.FC = () => {
  const groups = useGroupsStore(state => state.groups)
  const deleteGroup = useGroupsStore(state => state.deleteGroup)
  const { user } = useAuth()
  const canManageGroups = canManageClientGroups(user)

  const [isFormOpen, setIsFormOpen] = useState(false)
  const [selectedGroup, setSelectedGroup] = useState<Group | null>(null)
  const [deleteGroupId, setDeleteGroupId] = useState<string | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)

  const handleCreate = () => {
    setSelectedGroup(null)
    setIsFormOpen(true)
  }

  const handleEdit = useCallback((group: Group) => {
    setSelectedGroup(group)
    setIsFormOpen(true)
  }, [])

  const handleDeleteClick = useCallback((id: string) => {
    setDeleteGroupId(id)
  }, [])

  const handleDeleteConfirm = async () => {
    if (!deleteGroupId) return

    setIsDeleting(true)
    try {
      await deleteGroup(deleteGroupId)
      setDeleteGroupId(null)
      toastSuccess('Groupe supprimé avec succès')
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error ? error.message : 'Erreur lors de la suppression du groupe'
      toastError(errorMessage)
    } finally {
      setIsDeleting(false)
    }
  }

  const handleDeleteCancel = () => {
    setDeleteGroupId(null)
  }

  const handleFormClose = () => {
    setIsFormOpen(false)
    setSelectedGroup(null)
  }

  const handleFormSuccess = () => {
    handleFormClose()
  }

  const columnDefs: ColDef[] = useMemo(
    () =>
      getGroupsColumnDefs({
        handleEdit,
        handleDeleteClick,
        canEdit: canManageGroups,
      }),
    [handleEdit, handleDeleteClick, canManageGroups]
  )

  const defaultColDef: ColDef = useMemo(
    () => ({
      sortable: true,
      filter: true,
      resizable: true,
      flex: 1,
      minWidth: 100,
    }),
    []
  )

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold text-[var(--color-text-primary)]">
          Liste des groupes
        </h2>
        {canManageGroups && <Button onClick={handleCreate}>Nouveau groupe</Button>}
      </div>

      <div className="bg-white rounded-lg border border-[var(--color-border)] shadow-sm overflow-hidden">
        <div
          className="ag-theme-alpine"
          style={{ height: '600px', width: '100%', display: 'flex', flexDirection: 'column' }}
        >
          <AGTableWrapper collection="groups">
            <AgGridReact
              theme="legacy"
              rowData={groups}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              pagination={true}
              paginationPageSize={20}
              paginationPageSizeSelector={[10, 20, 50, 100]}
              animateRows={true}
              enableCellTextSelection={true}
              ensureDomOrder={true}
              suppressCellFocus={false}
              headerHeight={48}
              rowHeight={48}
              localeText={agGridLocaleFr}
            />
          </AGTableWrapper>
        </div>
      </div>

      {isFormOpen && (
        <GroupFormModal
          group={selectedGroup}
          onClose={handleFormClose}
          onSuccess={handleFormSuccess}
        />
      )}

      {deleteGroupId && (
        <Modal
          title="Supprimer le groupe"
          isDanger={true}
          confirmButtonText="Supprimer"
          onCancel={handleDeleteCancel}
          onConfirm={handleDeleteConfirm}
          isLoading={isDeleting}
          size="sm"
        >
          <p className="text-[var(--color-text-primary)]">
            Êtes-vous sûr de vouloir supprimer ce groupe ? Cette action est irréversible.
          </p>
        </Modal>
      )}
    </div>
  )
}
